import { routing, type AppLocale } from './routing'

// Route prefixes that live OUTSIDE the [locale] segment. App, auth, and
// platform internals must never carry a locale prefix in their canonical URL.
//
// Order matters for `isAppRoute` — longer prefixes first so `/welcome-pro`
// isn't shadowed by `/welcome`.
export const APP_ROUTE_PREFIXES = [
  '/p',
  // Public standalone camera-motion share page (/m/<motionId>). Locale-less and
  // auth-optional like /p, so the proxy must treat it as an app route and skip
  // next-intl rewriting.
  '/m',
  '/projects',
  // "Check a file" on its own (/lineage/check): locale-less, authenticated.
  '/lineage',
  // Billing receipts live in the locale-less app tree.
  '/generations',
  // Explicit Personal workspace links. Bare /projects and /library resume the
  // remembered workspace; /personal/* is a durable external destination.
  '/personal',
  '/team',
  // One prefix covers every settings page (present and future).
  '/settings',
  // Legacy pre-IA paths: real page routes that permanently redirect into
  // /settings/* (query-forwarding stubs). They must stay app-routed or the
  // proxy hands them to the next-intl marketing middleware and the stubs
  // never run — breaking old bookmarks AND Stripe checkout return URLs.
  '/account',
  '/billing',
  '/blender-import',
  '/welcome-pro',
  '/welcome',
  '/quick-start',
  '/select-plan',
  '/sample',
  '/benchmark',
  '/timeline-performance',
  '/invites',
  '/a-moment-we-remember',
  '/learn',
  // Public documentation in the Learn chrome. Locale-less like /learn; the
  // old locale-prefixed marketing URLs (/ja/docs/mcp) 308-strip via step 2 of
  // the proxy. `/docs-imgs/*` (docs media) is not matched — matchesPrefix
  // requires `/docs` or `/docs/`.
  '/docs',
  // The Blender skill page: same public Learn-chrome surface as /docs.
  '/skills',
  '/auth',
  // Focused sign-in screen (desktop shell, deep links). Locale-less like /auth.
  '/login',
  // `martini login` sends people to /cli/auth to type the pairing code. It sits
  // under the (app) root layout like /auth, so without this prefix the proxy
  // hands it to the next-intl marketing tree and production answers 404.
  '/cli',
  // Internal Sentry smoke-test page. Lives under the (app) root layout and
  // carries no locale prefix, so the proxy must treat it as an app route and
  // skip next-intl rewriting (otherwise it resolves against the [locale] tree
  // and 404s).
  '/sentry-test-client',
  // Library — the workspace-level browse/manage surface for collections.
  // Lives under (app), locale-less, so the proxy must treat it as an app
  // route and skip next-intl rewriting.
  '/library',
  // Workflows — the workspace-level view of Actions workflows and their runs.
  // Same shape as /library: bare /workflows resumes the remembered workspace,
  // /personal/workflows and /team/<slug>/workflows name one.
  '/workflows',
  // Internal admin tool — also surfaced as "app" so AuthContext / LocaleSwitcher
  // refresh after a cookie change instead of leaving backstage stuck on the
  // stale server-resolved locale. The proxy bypasses it earlier via isInternalRoute.
  '/backstage',
  // In-context translation editor — owns its own root layout under
  // /translate/[locale]/... and is bypassed by isInternalRoute below.
  '/translate',
] as const

export const INTERNAL_ROUTE_PREFIXES = [
  '/api',
  '/backstage',
  '/translate',
  '/mcp',
  '/_next',
  '/_storage',
  '/_vercel',
  '/ingest',
  '/monitoring',
  '/.well-known',
  '/register',
  '/authorize',
  '/token',
] as const

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

export function isAppRoute(pathname: string): boolean {
  return APP_ROUTE_PREFIXES.some(prefix => matchesPrefix(pathname, prefix))
}

export function isInternalRoute(pathname: string): boolean {
  return INTERNAL_ROUTE_PREFIXES.some(prefix => matchesPrefix(pathname, prefix))
}

const LOCALE_PREFIX_PATTERN = new RegExp(`^/(${routing.locales.join('|')})(?:/|$)`)

// Strip a leading /{locale}/ from a pathname. Returns the stripped locale
// (if any) and the remainder. The remainder always starts with '/'.
//
// `/ja/p/abc` → { locale: 'ja', pathname: '/p/abc' }
// `/ja`        → { locale: 'ja', pathname: '/' }
// `/ja/`       → { locale: 'ja', pathname: '/' }
// `/p/abc`     → { locale: null, pathname: '/p/abc' }
export function stripLocalePrefix(pathname: string): { locale: AppLocale | null; pathname: string } {
  const match = pathname.match(LOCALE_PREFIX_PATTERN)
  if (!match) return { locale: null, pathname }
  const locale = match[1] as AppLocale
  const afterPrefix = pathname.slice(`/${locale}`.length)
  return { locale, pathname: afterPrefix === '' ? '/' : afterPrefix }
}

// Delete each exception when its locale launches site-wide.
export const EARLY_LOCALE_PAGES: Partial<Record<AppLocale, readonly string[]>> = {
  es: ['/production-calculator'],
}

export function isEarlyLocalePage(locale: AppLocale, pathname: string): boolean {
  // Sub-paths count too: the page's opengraph-image lives under it.
  return EARLY_LOCALE_PAGES[locale]?.some(page => pathname === page || pathname.startsWith(`${page}/`)) ?? false
}
